import * as React from 'react';
import { goTo, t } from '../../../services/HelperService';
import { FiUserPlus } from 'react-icons/fi';
import { WorkflowCardV2Renderer } from './WorkflowCardV2Renderer';
import { buildWorkflowCardContext, buildWorkflowCardQueries, parseWorkflowCardLayoutV2 } from './workflowCardRuntime';
import { getBuiltInWorkflowCard, parseBuiltInCardData } from './builtInCards';

/**
 * The card's frame as the board draws it: white, rounded, soft shadow, 12px 14px
 * inner padding and the 4px coloured left bar. Exported so the designer's preview
 * (WorkflowCardPreview) shows a render script inside EXACTLY this chrome — a design
 * that looks right in the preview looks right on the board. The AI prompt describes
 * this same frame (WorkflowCardSessionProfile) so scripts don't draw their own.
 */
export const workflowCardFrameStyle = {
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
  padding: '12px 14px',
  borderLeft: '4px solid #0d6efd'
};

const cardStyle = {
  ...workflowCardFrameStyle,
  marginBottom: 8,
  cursor: 'grab',
  transition: 'box-shadow 0.15s, opacity 0.15s, transform 0.15s',
  userSelect: 'none',
  position: 'relative'
};

const cardDraggingStyle = {
  ...cardStyle,
  opacity: 0.5,
  boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  transform: 'scale(0.97)'
};

const cardLockedStyle = {
  ...cardStyle,
  cursor: 'not-allowed',
  opacity: 0.6,
  borderLeft: '4px solid #adb5bd'
};

const cardPendingStyle = {
  ...cardStyle,
  cursor: 'progress',
  opacity: 0.75,
  borderLeft: '4px solid #fd7e14'
};

const titleStyle = {
  fontWeight: 600,
  fontSize: 13,
  marginBottom: 4,
  color: '#212529'
};

const descStyle = {
  fontSize: 12,
  color: '#6c757d',
  marginBottom: 6
};

const badgeContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 6
};

const badgeStyle = {
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 500,
  padding: '2px 7px',
  borderRadius: 10,
  background: '#e8f5e9',
  color: '#2e7d32',
  whiteSpace: 'nowrap'
};

const lockBadgeStyle = {
  ...badgeStyle,
  background: '#fff3e0',
  color: '#e65100'
};

const pendingBadgeStyle = {
  ...badgeStyle,
  background: '#fff3cd',
  color: '#9a6700'
};

const assignmentBadgeStyle = {
  ...badgeStyle,
  background: '#e3f2fd',
  color: '#1565c0'
};

const reassignButtonStyle = {
  position: 'absolute',
  top: 6,
  right: 6,
  width: 22,
  height: 22,
  borderRadius: 11,
  border: '1px solid #ced4da',
  background: '#fff',
  color: '#495057',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0
};

const openEntityButtonStyle = {
  ...reassignButtonStyle,
  right: 32
};

export const WorkflowKanbanCard = ({ card, stageNameByKey, isDragging, onDragStart, onDragEnd, onReassign }) => {
  const allowedKeys = card.allowedMoveStageKeys ?? [];
  const hasNoMoves = allowedKeys.length === 0;
  const isPendingMove = !!card._pendingMove;
  const assignedRepName = (card.assignedRepName ?? '').toString().trim();
  const assignedTeamName = (card.assignedTeamName ?? '').toString().trim();
  const hasNavigatePath = !!(card.navigatePath ?? '').trim();
  // Query-based card layout ({ version: 2, render }); null when the card has no design yet.
  const layoutV2 = React.useMemo(() => parseWorkflowCardLayoutV2(card.workflowCardLayoutJson), [card.workflowCardLayoutJson]);
  // Render context: this card's query rows (server-batched per board) plus the card facts.
  const v2Queries = React.useMemo(
    () => (layoutV2 ? buildWorkflowCardQueries(card.workflowCardData?.Queries ?? card.workflowCardData?.queries) : null),
    [layoutV2, card.workflowCardData],
  );
  const v2Card = React.useMemo(() => (layoutV2
    ? buildWorkflowCardContext({
      entityID: card.entityId,
      entityType: card.entityType,
      stageKey: card.currentStageKey,
      stageName: stageNameByKey?.get?.(card.currentStageKey) ?? card.currentStageKey,
      workflowName: card.title,
      title: card.description,
      description: card.description,
    })
    : null), [layoutV2, card.entityId, card.entityType, card.currentStageKey, card.title, card.description, stageNameByKey]);
  const BuiltInCard = getBuiltInWorkflowCard(card.builtInCardKey);
  const builtInCardData = React.useMemo(() => parseBuiltInCardData(card.builtInCardDataJson), [card.builtInCardDataJson]);

  const handleDragStart = (e) => {
    if (hasNoMoves || isPendingMove) {
      e.preventDefault();
      return;
    }
    // Interactive regions inside built-in cards (e.g. file note input) opt out of dragging.
    if (e.target?.closest?.('[data-no-drag="true"]')) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', card.id);
    onDragStart?.(card);
  };

  const handleDragEnd = () => {
    onDragEnd?.();
  };

  const resolvedStyle = hasNoMoves
    ? cardLockedStyle
    : (isPendingMove ? cardPendingStyle : (isDragging ? cardDraggingStyle : cardStyle));

  return (
    <div
      draggable={!hasNoMoves && !isPendingMove}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      style={resolvedStyle}
      title={
        isPendingMove
          ? t('Saving move...')
          : (hasNoMoves ? t('No manual transitions available for your roles') : undefined)
      }
    >
      <button
        type="button"
        draggable={false}
        style={reassignButtonStyle}
        title={t('Reassign')}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isPendingMove) {
            onReassign?.(card);
          }
        }}
      >
        <FiUserPlus size={12} />
      </button>
      {hasNavigatePath && (
        <button
          type="button"
          draggable={false}
          style={openEntityButtonStyle}
          title={t('Open record')}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isPendingMove) {
              void goTo(card.navigatePath);
            }
          }}
        >
          <span className="material-icons" style={{ fontSize: 12 }}>open_in_new</span>
        </button>
      )}
      {layoutV2 && card.workflowCardData ? (
        <WorkflowCardV2Renderer
          render={layoutV2.render}
          queries={v2Queries}
          card={v2Card}
          queryErrors={card.workflowCardData?.QueryErrors ?? card.workflowCardData?.queryErrors}
          fallback={(
            <>
              <div style={titleStyle}>{card.title}</div>
              <div style={descStyle}>{card.description}</div>
            </>
          )}
        />
      ) : BuiltInCard && builtInCardData ? (
        <BuiltInCard card={card} data={builtInCardData} />
      ) : (
        <>
          <div style={titleStyle}>{card.title}</div>
          <div style={descStyle}>{card.description}</div>
        </>
      )}
      <div style={badgeContainerStyle}>
        {!!assignedRepName && (
          <span style={assignmentBadgeStyle}>
            <span className="material-icons" style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 2 }}>person</span>
            {assignedRepName}
          </span>
        )}
        {!assignedRepName && !!assignedTeamName && (
          <span style={assignmentBadgeStyle}>
            <span className="material-icons" style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 2 }}>groups</span>
            {assignedTeamName}
          </span>
        )}
        {isPendingMove ? (
          <span style={pendingBadgeStyle}>
            <span className="material-icons" style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 2 }}>sync</span>
            {t('Saving...')}
          </span>
        ) : hasNoMoves ? (
          <span style={lockBadgeStyle}>
            <span className="material-icons" style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 2 }}>lock</span>
            {t('No moves')}
          </span>
        ) : (
          allowedKeys.map((key) => (
            <span key={key} style={badgeStyle}>
              <span className="material-icons" style={{ fontSize: 10, verticalAlign: 'middle', marginRight: 2 }}>arrow_forward</span>
              {stageNameByKey?.get?.(key) ?? key}
            </span>
          ))
        )}
      </div>
    </div>
  );
};
